import Image from "next/image";
import routes from "@/constants/routes";
import RecentPostCard from "./RecentPostCard";
import { useState } from "react";
import ArticleCard from "./ArticleCard";
import HighlightedArticle from "@/components/koach/Knowledge/components/HighlightedArticle";
import CarouselComponent from "@/components/koach/Shared/CarouselComponent";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import getConfig from "next/config";
import { ArticleContentProps } from "../types";
import moment from "moment";
import {
  getGuidesQuery,
  getGuideCategoriesQuery,
} from "@/services/cms/queries";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import Link from "next/link";
import {
  getGuideTextColor,
  getGuideBgColor,
} from "@/components/koach/Shared/Helper";

const ArticleContent = ({ guide }: ArticleContentProps): JSX.Element => {
  const [isCopied, setIsCopied] = useState(false);
  const { publicRuntimeConfig } = getConfig();
  const slug = guide?.attributes?.slug;
  const relatedGuides = guide?.attributes?.relatedGuides?.data || [];
  const addedOn = moment(guide?.attributes?.addedOn).format("DD MMMM YYYY");
  const author = guide?.attributes?.author?.data?.attributes?.name;
  const tags = guide?.attributes?.tags?.data || [];
  const textColor = getGuideTextColor(
    guide?.attributes?.category?.data?.attributes?.slug
  );
  const bgColor = getGuideBgColor(
    guide?.attributes?.category?.data?.attributes?.slug
  );

  const { data: recentGuidesResponse } = getGuidesQuery({
    populate: "category, cover",
    sort: "addedOn:desc",
    pagination: { start: 0, limit: 5 },
  });
  const recentGuides = recentGuidesResponse?.data || [];

  const { data: categoriesResponse } = getGuideCategoriesQuery();
  const categories = categoriesResponse?.data || [];

  const handleCopyClick = () => {
    navigator.clipboard.writeText(
      publicRuntimeConfig.NEXT_PUBLIC_URL + routes.guides + "/" + slug
    );
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };

  let content = guide?.attributes?.content;

  content = content.replace(/```[\s\S]*?```/g, (m) => m.replace(/\n/g, "\n "));

  // Support single line breaks outside of code blocks
  content = content.replace(/(?<=\n\n)(?![*-])\n/g, "&nbsp;\n ");

  // Support single line breaks within code blocks
  content = content.replace(/\n(?!\s*```)/g, "  \n");

  // Add space between bullet points
  content = content.replace(/(\n\s*[*-])/g, "\n\n$1");

  return (
    <div className="flex flex-col gap-2 md:gap-5 md:px-11 xl:px-32 my-12">
      <div className="font-medium cursor-pointer mb-8 lg:mb-6">
        <Link href={routes.guides}>
          <div className="flex items-center justify-start gap-3 font-bold text-magenta px-6 md:px-0">
            <Image
              className="rotate-180"
              src="/koach/icons/arrow-right.svg"
              width="14"
              height="14"
              alt="chevron"
            />
            Back
          </div>
        </Link>
      </div>

      {/* Title section*/}
      <h1 className="text-center font-semibold text-[32px] md:text-[48px] xl:text-[64px] leading-[1.1] px-4 md:px-24">
        {guide?.attributes?.title}
      </h1>

      {/* Subtitle section */}
      <div className="my-6 w-full flex flex-col md:flex-row md:justify-between gap-3 md:gap-5 text-center items-center">
        <div className={`rounded-full px-3 py-1 ${bgColor}`}>
          <p className={`text-sm font-bold cursor-pointer ${textColor}`}>
            <Link
              href={
                routes.guides +
                "?category=" +
                guide?.attributes?.category?.data?.attributes.slug
              }
            >
              {guide?.attributes?.category?.data?.attributes?.name}
            </Link>
          </p>
        </div>
        <p className="text-sm">
          {" "}
          {author} on {addedOn}{" "}
        </p>
      </div>

      {/* Article content Flex */}
      <div className="flex flex-col md:flex-row justify-between gap-5 md:gap-8 my-7">
        {/* Left div */}
        <div className="flex flex-col gap-10 md:w-4/5">
          {/* Article image */}
          <div className="relative w-full h-[25rem] md:py-[17%]">
            <Image
              src={
                guide?.attributes?.cover?.data?.attributes?.formats?.large?.url
              }
              objectFit="cover"
              alt={guide?.attributes?.title}
              layout="fill"
            />
          </div>
          <div className="w-full flex flex-col gap-5 text-base text-grey-500 px-4 md:px-0">
            <ReactMarkdown
              className={"markdownContent"}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              linkTarget="_blank"
            >
              {content}
            </ReactMarkdown>
          </div>

          {/* Share */}
          <div className="flex flex-col md:flex-row justify-between gap-6 md:gap-10 my-6 px-4 md:px-0">
            <div className="flex gap-4 text-center items-center">
              <p className="font-bold">Share:</p>
              <div className="relative flex items-center gap-5 pt-1">
                <div
                  className={`${
                    isCopied ? "flex" : "hidden"
                  } bg-magenta rounded-lg absolute -top-16 right-22 text-white p-5 z-20 w-fit min-w-fit`}
                >
                  Link copied
                </div>
                <a
                  onClick={() => {
                    handleCopyClick();
                  }}
                >
                  <Image
                    src="/koach/icons/copy-link.svg"
                    width="32"
                    height="32"
                    alt="link"
                  />
                </a>

                <FacebookShareButton
                  url={
                    publicRuntimeConfig.NEXT_PUBLIC_URL +
                    routes.guides +
                    "/" +
                    slug
                  }
                >
                  <Image
                    src="/social/facebook.svg"
                    width="32"
                    height="32"
                    alt="facebook"
                  />
                </FacebookShareButton>

                <LinkedinShareButton
                  url={
                    publicRuntimeConfig.NEXT_PUBLIC_URL +
                    routes.guides +
                    "/" +
                    slug
                  }
                  summary={guide.attributes.description}
                  title={guide.attributes.title}
                >
                  <Image
                    src="/social/linkedin.svg"
                    width="32"
                    height="32"
                    alt="linkedin"
                  />
                </LinkedinShareButton>

                <TwitterShareButton
                  url={
                    publicRuntimeConfig.NEXT_PUBLIC_URL +
                    routes.guides +
                    "/" +
                    slug
                  }
                  title={guide.attributes.title}
                >
                  <Image
                    src="/social/twitter.svg"
                    width="32"
                    height="32"
                    alt="twitter"
                  />
                </TwitterShareButton>
              </div>
            </div>
          </div>
        </div>

        {/* Right div */}
        <div className="flex flex-col gap-6 md:gap-14 px-5 md:px-0 md:w-1/5">
          {/* Categories */}
          <div className="bg-guides-paleUltraMarine py-5 px-4 flex flex-col gap-4 bg-opacity-30 rounded-xl">
            <h3 className="font-bold">Categories</h3>
            <div className="flex flex-row flex-wrap md:flex-col gap-4 ">
              {categories.map((category, index) => (
                <div className="text-base cursor-pointer hover:underline ">
                  <Link
                    href={
                      routes.guides + "?category=" + category.attributes.slug
                    }
                    key={index}
                    className=""
                  >
                    {category.attributes.name}
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Posts */}
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold">Recent posts</h3>
            <div className="flex flex-col gap-6">
              {recentGuides.map((guide, index) => (
                <div key={index} className="hover:underline">
                  <RecentPostCard baseUrl={routes.guides} guide={guide} />
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-col gap-4">
            <h3 className="font-bold">Tag Cloud</h3>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <p
                  key={index}
                  className="border p-4 rounded-3xl border-magenta"
                >
                  {tag.attributes.name}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* End of flex */}

      {/* Related blogs */}
      {relatedGuides.length > 0 && (
        <div className="py-4 px-2 md:px-0">
          <h2 className="font-bold text-2xl lg:text-3xl md:text-center px-4 mb-6 md:mb-12">
            Related Blogs
          </h2>
          <div className="flex flex-col md:flex-row gap-5">
            <div className="hidden md:grid grid-cols-2 xl:grid-cols-3 gap-7 w-full">
              {relatedGuides.map((guide, index) => (
                <div key={index}>
                  <ArticleCard baseUrl={routes.guides} guide={guide} />
                </div>
              ))}
            </div>

            <div className="md:hidden px-3">
              <CarouselComponent data={relatedGuides}>
                {(guide: any, index: number) => (
                  <HighlightedArticle baseUrl={routes.guides} guide={guide} />
                )}
              </CarouselComponent>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleContent;
